import { Text } from '@/components/animated-type/text';
import { AnnotatedType } from '@/components/annotated-type';
import { StoryHeader, StoryTagline, StoryTheme, StoryTitle } from '@/components/microstory/header';
import { Outro } from '@/components/microstory/outro';
import { StoryQuestion, useStoryQuestion } from '@/components/microstory/question';
import { StoriesNavigation } from '@/components/microstory/stories-navigation';
import { CenteredStoryText } from '@/components/microstory/text';
import { Theme, Themes } from '@/components/microstory/theme';
import { SeoHead } from '@/components/seo-head';
import { OnlineRisksChapter1 } from '@/components/stories/online-risks/chapter-1';
import { OnlineRisksChapter2 } from '@/components/stories/online-risks/chapter-2';
import { OnlineRisksChapter3 } from '@/components/stories/online-risks/chapter-3';
import data from '@/data/online-risks.json';
import { createGetServerSideProps } from '@/utils/create-get-server-side-props';
import { getStoryProps } from '@/utils/get-story-props';
import { useLayout } from '@/utils/use-layout';
import { useSharingImage } from '@/utils/use-sharing-image';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { InferGetServerSidePropsType } from 'next';

export const getServerSideProps = createGetServerSideProps(
  async () => ({ data: data as OnlineRisksData }),
  getStoryProps('online-risks', 'online-opportunities')
);

export default function OnlineRisks(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  useLayout('white');
  useLingui();

  const { storySlug, baseUrl, data, sharingUrl, linkedStoryIds, relatedStoryId } = props;

  const sharingImage = useSharingImage('online-risks');

  const pageTitle = t({ id: 'online-risks.title', message: 'Online risks' });
  const pageDescription = t({
    id: 'online-risks.description',
    message:
      'The internet has opened innumerable doors for users. But it also brings a host of risks—especially for young people.',
  });

  const answers = [
    { id: 'very', label: t({ id: 'online-risks.question.answer-1', message: 'Very concerned' }) },
    {
      id: 'somewhat',
      label: t({ id: 'online-risks.question.answer-2', message: 'Somewhat concerned' }),
    },
    {
      id: 'not',
      label: t({ id: 'online-risks.question.answer-3', message: 'Not concerned at all' }),
    },
  ];

  const { selectedAnswer, handleAnswer } = useStoryQuestion();

  return (
    <>
      <SeoHead
        baseUrl={baseUrl}
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        sharingImage={sharingImage}
      />

      <Theme theme={Themes.fire} hasBackButton>
        <StoryHeader
          backgroundId={storySlug}
          css={{
            backgroundPosition: 'center top',
            '@bp9': {
              backgroundSize: '100% auto',
            },
          }}
        >
          <StoryTheme>
            <Trans id="online-risks.theme">Digital tech</Trans>
          </StoryTheme>
          <StoryTitle>{pageTitle}</StoryTitle>
          <StoryTagline>
            <Trans id="online-risks.tagline">
              <Text type="heading">
                The internet has opened innumerable doors for users. But it also brings{' '}
                <span>a host of risks</span>—especially for young people.
              </Text>
            </Trans>
          </StoryTagline>
        </StoryHeader>

        <StoryQuestion
          question={t({
            id: 'online-risks.question.question',
            message:
              "When you're online, personal information such as your name, phone number and location can be collected and shared.",
          })}
          answerHeading={t({
            id: 'online-risks.question.answer-heading',
            message: 'How concerned are you about this?',
          })}
          answers={answers}
          onAnswer={handleAnswer}
          selectedAnswer={selectedAnswer}
          response={t({
            id: 'online-risks.question.response',
            message: "You're not alone!",
          })}
        />

        <OnlineRisksChapter1 data={data} />
        <OnlineRisksChapter2
          data={data.concerned_with_sharing_personal_info.very_concerned_breakdown}
        />

        <CenteredStoryText>
          <Trans id="online-risks.centered-text-1">
            Data privacy is just one worry about internet use.
          </Trans>
        </CenteredStoryText>

        <OnlineRisksChapter3 data={data.online_risks} />

        <Outro
          relatedStoryId={relatedStoryId}
          sharing={{
            url: sharingUrl,
            title: pageTitle,
            text: t({
              id: 'online-risks.sharing.text',
              message:
                'The internet has opened innumerable doors for users. But it also brings a host of risks—especially for young people.',
            }),
          }}
        >
          <Trans id="online-risks.closing-question">
            What changes do you think are needed to make the internet{' '}
            <AnnotatedType>a safer place</AnnotatedType> for children?
          </Trans>
        </Outro>
      </Theme>

      <StoriesNavigation storyIds={linkedStoryIds} />
    </>
  );
}
